import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { auth, getUserFromDatabase } from "shared/utils/firebase";
import { UserProfileProvider } from "shared/utils/UserProfileContext";
import { attachAuthSyncListener } from "shared/utils/sessionBridge";
import ScrollToTop from "shared/hooks/ScrollTop";

import CodeBox from "./UserSection/Practice/CodeBox/codebox";
import CodeList from "./UserSection/Practice/CodeTest/codeList";
import CodingPractice from "./UserSection/Practice/CodeTest/CodingPractice";
import Challenges from "./UserSection/Practice/Challenges/Challenges";
import Examples from "./UserSection/Practice/Examples/Examples";
import Auth from "shared/components/Auth/Auth";
import OnboardingModal from "shared/components/Auth/OnboardingModal";
import LandingPage from "./UserSection/Landing/LandingPage";
import ProfilePage from "./UserSection/Profile/ProfilePage";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [authReady, setAuthReady] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('codebox-theme') || 'dark');

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('codebox-theme', next);
  };

  const changeTheme = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('codebox-theme', newTheme);
  };

  const fetchUserDetails = async (uid, retries = 4) => {
    try {
      const details = await getUserFromDatabase(uid);
      if (details) {
        setUserDetails(details);
        return details;
      }
      if (retries > 0) {
        await new Promise((res) => setTimeout(res, 350));
        return fetchUserDetails(uid, retries - 1);
      }
    } catch (err) {
      console.warn("fetchUserDetails error:", err);
    }
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const unbindBridge = attachAuthSyncListener(auth);

    const handleProfileUpdate = (e) => {
      if (e.detail) {
        setUserDetails(prev => ({ ...prev, ...e.detail }));
      }
    };
    window.addEventListener('slx_user_profile_updated', handleProfileUpdate);

    const listener = auth.onAuthStateChanged((user) => {
      if (!user) {
        setIsAuthenticated(false);
        setUserDetails({});
        setAuthReady(true);
        return;
      }
      setIsAuthenticated(true);
      fetchUserDetails(user.uid);
      setAuthReady(true);
    });
    return () => {
      unbindBridge && unbindBridge();
      window.removeEventListener('slx_user_profile_updated', handleProfileUpdate);
      listener();
    };
  }, []);

  // Don't render until Firebase auth has resolved
  if (!authReady) return null;

  const requiresOnboarding = Boolean(
    isAuthenticated &&
    userDetails?.uid &&
    (
      userDetails.onboardingCompleted === false ||
      (userDetails.provider && userDetails.provider !== "email" && !userDetails.role) ||
      (userDetails.phoneVerified && (!userDetails.email || userDetails.name?.startsWith("User ")))
    )
  );

  return (
    <Router>
      <ScrollToTop>
        <UserProfileProvider initialUserDetails={userDetails} uid={userDetails?.uid || auth.currentUser?.uid}>
          {requiresOnboarding && (
            <OnboardingModal
              isOpen={true}
              userDetails={userDetails}
              onSuccess={(updated) => {
                setUserDetails((prev) => ({ ...prev, ...updated, onboardingCompleted: true }));
              }}
            />
          )}
          <Routes>
            {/* Landing Page */}
            <Route
              path="/page"
              element={
                <LandingPage
                  isAuthenticated={isAuthenticated}
                  userDetails={userDetails}
                  theme={theme}
                  toggleTheme={toggleTheme}
                />
              }
            />

            {/* User Profile Page */}
            <Route
              path="/profile"
              element={
                <ProfilePage
                  isAuthenticated={isAuthenticated}
                  userDetails={userDetails}
                  theme={theme}
                  toggleTheme={toggleTheme}
                />
              }
            />

            {/* IDE / CodeBox */}
            <Route
              path="/ide"
              element={
                <CodeBox
                  userDetails={userDetails}
                  auth={isAuthenticated}
                  theme={theme}
                  setTheme={changeTheme}
                />
              }
            />

            {/* Exercise List Page */}
            <Route
              path="/exercise"
              element={
                <CodeList
                  userDetails={userDetails}
                  auth={isAuthenticated}
                  theme={theme}
                  toggleTheme={toggleTheme}
                />
              }
            />

            {/* Challenges List Page */}
            <Route
              path="/challenges"
              element={
                <Challenges
                  userDetails={userDetails}
                  auth={isAuthenticated}
                  theme={theme}
                  toggleTheme={toggleTheme}
                />
              }
            />

            {/* Examples List Page */}
            <Route
              path="/examples"
              element={
                <Examples
                  userDetails={userDetails}
                  auth={isAuthenticated}
                  theme={theme}
                  toggleTheme={toggleTheme}
                />
              }
            />

            {/* Coding Practice Session Page */}
            <Route
              path="/exercise/:language"
              element={
                <CodingPractice
                  userDetails={userDetails}
                  auth={isAuthenticated}
                  theme={theme}
                  toggleTheme={toggleTheme}
                />
              }
            />

            {/* Auth pages */}
            <Route path="/login" element={<Auth signup={false} />} />
            <Route path="/signup" element={<Auth signup={true} />} />

            {/* Default: redirect "/" to "/page" */}
            <Route path="/" element={<Navigate to="/page" replace />} />
            <Route path="*" element={<Navigate to="/page" replace />} />
          </Routes>
        </UserProfileProvider>
      </ScrollToTop>
    </Router>
  );
}

export default App;
